import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, DbPrefabDetail } from '../services/adminApi'
import styles from './PrefabsPage.module.css'

const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max) + '…' : s)

export default function PrefabsPage() {
  const { apiKey } = useAdmin()

  const [prefabs, setPrefabs] = useState<DbPrefabDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCharacter, setNewCharacter] = useState('')
  const [characterNames, setCharacterNames] = useState<string[]>([])
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    const api = createAdminApi()
    setLoading(true)
    setError('')
    api.listDbPrefabs()
      .then(setPrefabs)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
    // Character options for the create form. Character rows are versioned
    // (Myra v1/v2 = separate rows); prefabs scope by the lowercased NAME, so
    // dedupe case-insensitively.
    api.getCharacters()
      .then(chars => {
        const names = [...new Set(chars.map(c => c.name.trim().toLowerCase()))].sort()
        setCharacterNames(names)
      })
      .catch(() => setCharacterNames([]))
  }, [apiKey])

  async function handleCreate() {
    if (!newName.trim() || !newCharacter) return
    const api = createAdminApi()
    setCreateError('')
    try {
      const t = await api.createDbPrefab(newCharacter, newName.trim(), [], '', [])
      window.location.hash = '#prefabs/' + t.id
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Prefabs</h1>
        <div className={styles.headerStats}>
          {!loading && <span className={styles.statPill}>{prefabs.length} in DB</span>}
          <button className={styles.createBtn} onClick={() => { setCreating(p => !p); setCreateError('') }}>
            + New Prefab
          </button>
        </div>
      </header>

      {creating && (
        <div className={styles.createBar}>
          <select
            className={styles.createInput}
            value={newCharacter}
            onChange={e => setNewCharacter(e.target.value)}
            aria-label="Character"
          >
            <option value="">Character…</option>
            {characterNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <input
            className={styles.createInput}
            placeholder="Prefab name (unique per character)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
            autoFocus
          />
          <button className={styles.saveBtn} onClick={handleCreate} disabled={!newName.trim() || !newCharacter}>Create</button>
          <button className={styles.cancelBtn} onClick={() => { setCreating(false); setNewName(''); setCreateError('') }}>Cancel</button>
          {createError && <span className={styles.saveError}>{createError}</span>}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.content}>
        {loading ? (
          <p className={styles.empty}>Loading…</p>
        ) : prefabs.length === 0 ? (
          <p className={styles.empty}>No prefabs in DB. Create one above or configure via the Admin page.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Character</th>
                <th>Phrases</th>
                <th>Not For</th>
                <th>Alternate</th>
                <th>Stories</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {prefabs.map(t => (
                <tr
                  key={t.id}
                  className={styles.row}
                  onClick={() => { window.location.hash = '#prefabs/' + t.id }}
                >
                  <td className={styles.nameCell}>
                    <a href={'#prefabs/' + t.id} onClick={e => e.stopPropagation()}>
                      {t.name}
                    </a>
                  </td>
                  <td>{t.characterName || '—'}</td>
                  <td>{t.phrases.length ? truncate(t.phrases.join(' · '), 80) : '—'}</td>
                  <td>{t.notFor.length ? truncate(t.notFor.join(' · '), 80) : '—'}</td>
                  <td>{t.alternate || '—'}</td>
                  <td>{t.stories.length} {t.stories.length === 1 ? 'story' : 'stories'}</td>
                  <td>{new Date(t.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
